<template>
  <EmptyPageContent
    v-if="!loading && !adyenPayouts.length"
    imageName="history"
    :text="emptyStateText"
  />
  <template v-else>
    <div :class="$style.scroll">
      <BaseTable
        :headers="headers"
        :rows="rows"
        v-test="'sp-payout-history-table'"
      />
    </div>
    <LoadMore
      v-show="!allDataFetched"
      @shown="fetchMore"
    />
  </template>
</template>

<script
  setup
  lang="ts"
>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePagination } from '@/apollo/pagination';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import type { AdyenPayout, Currency } from '@/types';
import { GET_ADYEN_PAYOUTS } from '../graphql';
import { walletName } from './wallet-share';

const props = defineProps<{
  balanceAccountId: number | null;
  multipleWallets: boolean;
}>();

const { t } = useI18n();

const { adyenPayouts, loading, allDataFetched, fetchMore, variables } =
  usePagination({
    fieldName: 'adyenPayouts',
    query: GET_ADYEN_PAYOUTS,
  });

// Changing the variables refetches from the first page.
watch(
  () => props.balanceAccountId,
  (balanceAccountId) => {
    if (balanceAccountId) {
      variables.balanceAccountId = balanceAccountId;
    } else {
      delete variables.balanceAccountId;
    }
  },
  { immediate: true },
);

const emptyStateText = computed(() => {
  const key = props.balanceAccountId ? 'empty_state_wallet' : 'empty_state';

  return {
    title: t(`register.salonized_pay.dashboard.payout_history.${key}.title`),
    description: t(
      `register.salonized_pay.dashboard.payout_history.${key}.description`,
    ),
  };
});

const showWallet = computed(
  () => props.multipleWallets && !props.balanceAccountId,
);

const destination = (payout: AdyenPayout) => {
  const last4 = payout.adyenTransferInstrument?.accountNumberLast4;

  return last4 ? `•••• ${last4}` : '-';
};

const headers = computed(() => [
  t('global.date'),
  ...(showWallet.value
    ? [t('register.salonized_pay.dashboard.payout_history.wallet')]
    : []),
  ...(props.multipleWallets
    ? [t('register.salonized_pay.dashboard.payout_history.to')]
    : []),
  t('global.amount'),
  t('global.status'),
  t('global.reference'),
  t('global.reason'),
]);

const statusColor = (status?: string | null) => {
  switch (status) {
    case 'booked':
      return 'success';
    case 'failed':
      return 'error';
    case 'returned':
      return 'warning';
    default:
      return 'default';
  }
};

const statusText = (status?: string | null) =>
  status
    ? t(`register.salonized_pay.dashboard.payout_history.status.${status}`)
    : '-';

const rows = computed(() =>
  adyenPayouts.value.map((payout: AdyenPayout) => ({
    id: payout.id,
    cells: [
      filters.date(payout.transferredAt || ''),
      ...(showWallet.value
        ? [payout.balanceAccount ? walletName(payout.balanceAccount) : '-']
        : []),
      ...(props.multipleWallets ? [destination(payout)] : []),
      filters.currency(
        payout.amountValue ?? 0,
        payout.amountCurrency as Currency,
      ),
      {
        label: {
          text: statusText(payout.status),
          color: statusColor(payout.status),
        },
      },
      payout.reference ?? '',
      payout.reason ?? '',
    ],
  })),
);
</script>

<style
  lang="scss"
  module
>
// With the wallet column the table is wider than the dashboard, so it scrolls sideways
.scroll {
  overflow-x: auto;
}
</style>
